import { readOnlyAnnouncementID } from "#skyward_global";
import { formatServerDate, parseDate } from "./skyward.base.time";
import * as codeEel from "./skyward.ui.codeeel";
import * as colorPicker from "./skyward.ui.colorPicker";
import { addMask, applyYearMask, setValue as dataMaskSetValue, getUnmaskedValue } from "./skyward.ui.datamask";
import * as dmse from "./skyward.ui.distinctMultiSelectEEL.values";
import { registerEvents } from "./skyward.ui.events";
import { alignTitles } from "./skyward.ui.fieldset";
import * as htmlComboBox from "./skyward.ui.htmlComboBox";
import * as htmlEditor from "./skyward.ui.htmlEditor";
import * as multipleSelectBox from "./skyward.ui.multipleSelectBox";
import * as textArea from "./skyward.ui.textArea";
import * as tileViewEEL from "./skyward.ui.tileViewEEL";
import { JQueryInput } from "./types";
import { parseDateInput } from "./vue/datePicker/DatePicker";

const skyward = window.skyward;

// This file will attempt to restore state to data bound input, select, eel, and codeeel type controls
export function getValueToString(element: JQueryInput)
{
    const val = getValue(element);
    return (typeof val.safeToDateString === "function") ? val.safeToDateString() : val.toString();
}

export function getValue(elementArg: JQueryInput)
{
    const element = $(elementArg);

    const elementData = element.data();
    let val;

    if (element.is(":radio"))
    {
        if (element.is(":checked"))
        {
            val = element.val();
        }
    }
    else if (element.is(":checkbox"))
    {
        if (element.hasClass("valuecheckbox"))
        {
            if (element.is(":checked"))
            {
                val = element.val();
            }
        }
        else
        {
            val = element.is(":checked");
        }
    }
    else if (element.hasClass("masked"))
    {
        val = getUnmaskedValue(element);
    }
    else if (element.hasClass("relativeDate"))
    {
        val = elementData.relativeDate;
    }
    else if (element.is("[type=date]")) //currently only in mobile access
    {
        // value will be an ISO-formatted date (YYYY-MM-DD) or an empty string
        const value = element.val() as string;
        // Date-only values are interpreted as UTC, but date+time is interpreted as local time.
        let newVal = value ? parseDate(value + "T00:00:00") : new Date(NaN);

        // Temp fix for issue with iOS 14 (not able to trigger date picker) (as of 11/16/2020)
        if (newVal.toString() === new Date(NaN).toString())
        {
            newVal = skyward.datepicker.parseDateString(value)
        }
        return newVal;
    }
    else if (element.hasClass("datepicker"))
    {
        val = skyward.datepicker.parseDateString(element.val() as string);
    }
    else if (element.is("sky-datepicker") || element.is("sky-datetimepicker"))
    {
        val = element.prop("valueAsDate");
    }
    else if (element.hasClass("datePicker") || element.hasClass("dateTimePicker"))
    {
        const container = element.parent();
        val = container.prop("valueAsDate");
    }
    else if (element.closest('.quickEntryText').length !== 0)
    {
        val = element.closest('.quickEntryText').find('.codeEELDescription').encodedVal();
    }
    else if (element.hasClass('codeEEL'))
    {
        val = element.find('.codeEELValue').val();
    }
    else if (element.hasClass('tileViewEEL'))
    {
        val = element.find('.tileViewEEL_value').val();
    }
    else if (element.hasClass('htmlComboBox'))
    {
        val = element.find('.htmlComboBox_value').val();
    }
    else if (element.is(".radioButtonGroupValue,.radioButtonGroup"))
    {
        val = skyward.radioButtonGroup.getValue(element);
    }
    else if (element.hasClass('distinctMultiSelectEEL'))
    {
        const backingData = element.find(".dmsBackingData");
        val = dmse.get(backingData);
    }
    else if (element.hasClass('dmsBackingData'))
    {
        val = dmse.get(element);
    }
    else if (element.hasClass('multiSelectBackingData'))
    {
        val = multipleSelectBox.getValue(element);
    }
    else if (element.hasClass('raceEthnicityVerification'))
    {
        val = skyward.onlineform.raceEthnicityGetValue(element);
    }
    else if (element.hasClass("button") && element.hasClass("useChangeTracking"))
    {
        val = elementData.value;
    }
    else if (element.hasClass('colorPicker'))
    {
        const color = element.spectrum('get');
        val = (color == null) ? "" : color.toString(elementData.colorFormat).replace(/\s/g, "");
    }
    else if (element.hasClass("parametermapper"))
    {
        val = skyward.parametermapper.getValue(element);
    }
    else if (element.hasClass("ckeditor"))
    {
        if (CKEDITOR.instances[element.attr("id")!])
        {
            val = skyward.extensions.encodeHtml(CKEDITOR.instances[element.attr("id")!].getData());
        }
        else
        {
            val = element.encodedVal();
        }
    }
    else if (element.hasClass("dashboardItemSizer__inner") || element.hasClass("dashboardItemSizer__container"))
    {
        val = element.find("#DashboardItemSize").val();
    }
    else if (element.hasClass("accountDimensionEEL"))
    {
        val = element.find(".accountDimensionEELValue").val();
    }
    else if (htmlEditor.isHtmlEditorElement(element))
    {
        val = htmlEditor.getEncodedValue(element);
    }
    else
    {
        if (element.val() === null)
        {
            val = null;
        }
        else
        {
            val = element.encodedVal();
        }
    }

    return val;
}

/**
 * Get the row data hash for a filterable control. This is the security hash indicating that the value is valid.
 * It must be submitted as {field}_hash to pass editable field validation.
 * @param elementArg The control
 * @returns If set: the control's row data hash. If not set: null. If the control is not filterable: undefined.
 */
export function getRowDataHash(elementArg: JQueryInput): string | undefined | null
{
    const element = $(elementArg);
    let foundElement: JQuery<HTMLElement>;
    if (element.hasClass("codeEEL"))
    {
        foundElement = element.find(".codeEELValue");
    }
    else if (element.hasClass("distinctMultiSelectEEL"))
    {
        foundElement = element.find(".dmsBackingData");
    }
    else if (element.hasClass("tileViewEEL"))
    {
        foundElement = element.find(".tileViewEEL_value");
    }
    else if (element.hasClass("accountDimensionEEL"))
    {
        foundElement = element.find(".accountDimensionEELValue");
    }
    else if (element.hasClass("codeEELValue") || element.hasClass("dmsBackingData") || element.hasClass("tileViewEEL_value") || element.hasClass("accountDimensionEELValue"))
    {
        foundElement = element;
    }
    else
    {
        return undefined;
    }
    return foundElement.data("selectedValueHash") as string ?? null;
}

// this can be used when you need to use the value as part of a larger object that will be encoded
export function getUnencodedValue(elementArg: JQueryInput)
{
    const element = $(elementArg);
    const value = getValue(element);

    if (value == null)
    {
        return null;
    }

    if (value instanceof Date)
    {
        if (isNaN(value.getTime()))
        {
            return null;
        }
        const isDatePicker = element.is(".datepicker,.datePicker") || element.is("sky-datepicker")
        return formatServerDate(value, !isDatePicker);
    }

    if (Array.isArray(value))
    {
        return value.map(v => skyward.extensions.decodeHtml(v));
    }

    if (typeof value === "object")
    {
        const unencodedObject: { [key: string]: string } = {};
        for (const key in value)
        {
            const unencodedKey = skyward.extensions.decodeHtml(key);
            unencodedObject[unencodedKey] = skyward.extensions.decodeHtml(value[key]);
        }
        return unencodedObject;
    }

    return skyward.extensions.decodeHtml(value as string);
}

export function getOriginalValue(elementArg: JQueryInput)
{
    const element = $(elementArg);

    const elementData = element.data();
    let val;

    if (element.hasClass("datepicker"))
    {
        // We don't know if this was an absolute or relative datepicker originally, so try both
        val = parseDate(element.data("original-value"));
        if (!skyward.datepicker.isValidDateObject(val) && element.hasClass("hasRelativeDatePicker"))
        {
            val = element.data("originalRelativeDate");
        }
    }
    else if (element.is("sky-datepicker,sky-datetimepicker"))
    {
        const originalValue = element.data().originalValue;
        val = originalValue ? parseDateInput(originalValue) : null;
    }
    else if (element.is(".datePicker,.dateTimePicker"))
    {
        const control = element.closest(".dateTimePickerWrapper");
        const originalValue = control.data().originalValue;
        val = originalValue ? parseDateInput(originalValue) : null;
    }
    else if (element.is(".timePicker"))
    {
        const control = element.closest(".dateTimePickerWrapper");
        val = control.data().originalValue;
    }
    else if (element.is(":checkbox") && element.hasClass("valuecheckbox"))
    {
        val = $.inArray(element.attr("data-original-value"), ["true", "indeterminate"]) === -1 ? undefined : element.attr("data-original-value");
    }
    else if (element.is("[data-mask]"))
    {
        const originalValueMasked = element.attr("data-original-value");
        const copy = element.clone();
        addMask(copy, elementData.mask);
        dataMaskSetValue(copy, originalValueMasked);
        val = getUnmaskedValue(copy);
    }
    else if (element.is(".radioButtonGroupValue,.radioButtonGroup"))
    {
        val = skyward.radioButtonGroup.getOriginalValue(element);
    }
    else if (element.hasClass('distinctMultiSelectEEL'))
    {
        const backingData = element.find(".dmsBackingData");
        val = dmse.getOriginal(backingData);
    }
    else if (element.hasClass('dmsBackingData'))
    {
        val = dmse.getOriginal(element);
    }
    else if (element.hasClass('multiSelectBackingData'))
    {
        val = multipleSelectBox.getOriginalValue(element);
    }
    else if (element.hasClass("listSortOrder"))
    {
        val = JSON.stringify(element.data("originalValue"));
    }
    else if (element.hasClass('codeEEL'))
    {
        val = element.find('.codeEELValue').data("original-value");
    }
    else if (htmlEditor.isHtmlEditorElement(element))
    {
        val = htmlEditor.getOriginalValue(element);
    }
    else
    {
        // Using data() automatically runs a JSON parse on any string beginning with "{", so any text field that contains valid JSON cannot be
        // directly compared with its current value.
        // *Setting* something via data() doesn't set the attribute to match, so we check if the data() value could have come from the
        // data-original-value attribute, and return its value if so.
        val = element.data("original-value");
        if (val instanceof Object && val !== null)
        {
            try
            {
                const originalData = element.attr("data-original-value");
                const parsed = JSON.parse(originalData || "");
                if (skyward.global.deepEquals(parsed, val))
                {
                    val = originalData;
                }
            }
            catch (_error) { /* JSON parse failed */ }
        }
    }

    return val;
}

export function setDataAttribute(elementArg: JQueryInput, attribute: string | undefined, value: any)
{
    const element = $(elementArg);

    //Check to see if attribute exists, don't add a new one
    if (attribute !== undefined && typeof element.data(attribute) !== 'undefined')
    {
        element.data(attribute, value);
    }
}

export function setValue(elementArg: JQueryInput, value: any, suppressChangeEvent = false): Promise<void> | void
{
    const element = $(elementArg);

    if (!suppressChangeEvent && element.hasClass("suppressChangeEvent"))
    {
        suppressChangeEvent = true;
    }

    if (element.is("img"))
    {
        element.prop("src", value);
        if (!suppressChangeEvent)
        {
            element.trigger("change");
        }
    }
    else if (element.is(":radio"))
    {
        for (let i = 0; i < element.length; i++)
        {
            const radioButton = $(element[i]);
            if (radioButton.val() == value)
            {
                radioButton.prop("checked", true);
                if (!suppressChangeEvent)
                {
                    radioButton.trigger("change");
                }
            }
        }
    }
    else if (element.hasClass("radioSet"))
    {
        element.find(':radio').each(function ()
        {
            const input = $(this);
            if (input.val() == value)
            {
                if (input.prop("checked") != true)
                {
                    input.prop("checked", true);
                    if (!suppressChangeEvent)
                    {
                        input.trigger("change");
                    }
                }
            }
            else
            {
                if (input.prop("checked") != false)
                {
                    input.prop("checked", false);
                    if (!suppressChangeEvent)
                    {
                        input.trigger("change");
                    }
                }
            }
        });
    }
    else if (element.is(":checkbox"))
    {
        element.prop("checked", typeof value === "string" ? (value.toLowerCase() === "true") : value);

        if (!suppressChangeEvent)
        {
            if (element.hasClass('labeledToggleSwitch__input'))
            {
                // Needed for Vue's reactivity
                element[0].dispatchEvent(new Event('change'));
            }
            else
            {
                element.trigger('change');
            }
        }
    }
    else if (element.is("[type=date]"))
    {
        if (!value)
        {
            element.val('');
            return;
        }
        const date = parseDate(value);
        const year = date.getFullYear();
        const month = ("0" + (date.getMonth() + 1)).slice(-2);
        const day = ("0" + date.getDate()).slice(-2);
        element.val([year, month, day].join('-'));
    }
    else if (element.is(".datePicker,.dateTimePicker,sky-datepicker,sky-dateTimePicker"))
    {
        const container = element.is("sky-datepicker,sky-dateTimePicker") ? element.filter("sky-datepicker,sky-dateTimePicker") : element.parent();
        const input = element.is("sky-datepicker,sky-dateTimePicker") ? element.find("input") : element.filter("input");
        const date = parseDate(value);
        container.prop("valueAsDate", date.isValidDate() ? date : null);

        if (!suppressChangeEvent)
        {
            input.trigger("change");
        }
    }
    else if (element.closest(".dateTimePickerModal").length > 0)
    {
        element.val(value);

        if (!suppressChangeEvent)
        {
            element[0].dispatchEvent(new Event('change'));
        }
    }
    else if (element.hasClass("datepicker"))
    {
        const datepickerDate = parseDate(value);
        if (skyward.datepicker.isValidDateObject(datepickerDate))
        {
            element.val($.datepicker.formatDate(element.hasClass("browseSearchBox") ? "mm/dd/yy" : "mm/dd/yy DD", datepickerDate));
        }
        else
        {
            if (element.hasClass("hasRelativeDatePicker"))
            {
                const relativeDate = skyward.relativedatepicker.parseRelativeDateString(value);
                if (relativeDate !== null)
                {
                    const datepickerId = element.attr("id")!;
                    skyward.relativedatepicker.setValue(datepickerId, relativeDate);
                    if (!suppressChangeEvent)
                    {
                        skyward.relativedatepicker.getRelativeDateDisplay(datepickerId).trigger("change");
                    }
                    return;
                }
            }
            element.val("");
        }

        if (!suppressChangeEvent)
        {
            element.trigger("change");
        }
    }
    else if (element.is("label") || element.hasClass("visualLabel") || element.hasClass("fieldValidationMessage"))
    {
        element.text(value);
        if (!suppressChangeEvent)
        {
            alignTitles(element);
            element.trigger("change");
        }
    }
    else if (element.is('[type=time]'))
    {
        if (!value)
        {
            element.val('');
            return;
        }
        let hour: number | undefined, minute: number | undefined, isPm: boolean | undefined;
        const time = parseDate(value);

        if (!isNaN(time.getTime()))
        {
            hour = time.getHours();
            minute = time.getMinutes();
        }
        else if (value)
        {
            value.split(" ").map(function (x: string)
            {
                if (x.indexOf(':') === 2)
                {
                    const timeParts = x.split(':');
                    hour = parseInt(timeParts[0]);
                    minute = parseInt(timeParts[1]);
                }
                else
                {
                    isPm = x.toLowerCase() === 'pm';
                }
            });

            if (isPm && hour && !isNaN(hour))
            {
                hour += 12;
            }
        }

        value = ("0" + hour).slice(-2) + ':' + ("0" + minute).slice(-2);
        element.val(value);

    }
    else if (element.hasClass("codeEEL"))
    {
        return codeEel.setCodeEELValue(element, value, suppressChangeEvent);
    }
    else if (element.hasClass("codeEELValue"))
    {
        return codeEel.setCodeEELValue(element.closest(".codeEEL"), value, suppressChangeEvent);
    }
    else if (element.hasClass("tileViewEEL"))
    {
        return tileViewEEL.setValue(element, value, suppressChangeEvent);
    }
    else if (element.hasClass("tileViewEEL_value"))
    {
        return tileViewEEL.setValue(element.parent(), value, suppressChangeEvent);
    }
    else if (element.hasClass("htmlComboBox"))
    {
        htmlComboBox.populateBoxes(element, value);
    }
    else if (element.hasClass('radioButtonGroupValue') || element.hasClass('radioButtonGroup'))
    {
        skyward.radioButtonGroup.setValue(element, value, suppressChangeEvent);
    }
    else if (element.hasClass('distinctMultiSelectEEL'))
    {
        const backingData = element.find(".dmsBackingData");
        return dmse.set(backingData, value, suppressChangeEvent);
    }
    else if (element.hasClass("dmsBackingData"))
    {
        return dmse.set(element, value, suppressChangeEvent);
    }
    else if (element.hasClass('multiSelectBackingData'))
    {
        multipleSelectBox.populate(element, value, suppressChangeEvent);
    }
    else if (element.hasClass("dashboardItemSizer__inner") || element.hasClass("dashboardItemSizer__container"))
    {
        const dashboardItemSize = element.find("#DashboardItemSize");
        dashboardItemSize.val(value);
        if (!suppressChangeEvent)
        {
            dashboardItemSize.trigger("change");
        }
    }
    else if (element.hasClass("filterViewValue"))
    {
        remask(element, value);

        if (value === null || value === "")
        {
            element.data("filterCondition", "");
            skyward.filterbuilder.updateFilterViewerDisplay(element);
            if (!suppressChangeEvent)
            {
                element.trigger("change");
            }
        }
        else
        {
            $.ajax(
                {
                    url: skyward.global.virtualDir + "SkySys/Filter/ConvertToJSON",
                    type: "POST",
                    dataType: "JSON",
                    data:
                    {
                        xml: skyward.extensions.encodeHtml(value)
                    },
                    success: function (data)
                    {
                        element.data("filterCondition", data || "");
                        skyward.filterbuilder.updateFilterViewerDisplay(element);
                        if (!suppressChangeEvent)
                        {
                            element.trigger("change");
                        }
                    }
                });
        }
    }
    else if (htmlEditor.isHtmlEditorElement(element))
    {
        htmlEditor.setValue(element, value, suppressChangeEvent);
    }
    else if (element.is("textarea"))
    {
        if (!textArea.setValuePreservingUndo(element, value))
        {
            remask(element, value);
        }

        if (!suppressChangeEvent)
        {
            element.trigger("change");
        }
    }
    else
    {
        if (element.data("spectrum.id") !== undefined)  //Check if it's an initialized ColorPicker input
        {
            $(element).spectrum("set", value);
        }
        else  //All other elements
        {
            remask(element, value);
        }

        if (!suppressChangeEvent)
        {
            element.trigger("change");
        }
    }
}

/**
* Copy a value and all attendant hashes and display values from one control to an identical control of the same type.
*/
export function copyValue(source: JQueryInput<HTMLElement>, dest: JQueryInput<HTMLElement>, suppressChangeEvent: boolean = false)
{
    const sourceElem = $(source);
    const destElem = $(dest);
    if (sourceElem.hasClass("codeEEL"))
    {
        codeEel.copyValue(sourceElem, destElem, suppressChangeEvent);
    }
    else if (sourceElem.hasClass("tileViewEEL"))
    {
        tileViewEEL.copyValue(sourceElem, destElem, suppressChangeEvent);
    }
    else if (sourceElem.hasClass("dmsBackingData"))
    {
        dmse.copyValue(sourceElem, destElem, suppressChangeEvent);
    }
    else
    {
        setValue(destElem, getValue(sourceElem), suppressChangeEvent);
    }
}

export function disable(control: JQueryInput, idPrefix?: string)
{
    return toggle(control, false, idPrefix);
}

export function enable(control: JQueryInput, idPrefix?: string)
{
    return toggle(control, true, idPrefix);
}

export function toggle(controlArg: JQueryInput, isEnabled: boolean, idPrefix?: string)
{
    let calculateControl = controlArg;
    if (!(calculateControl instanceof $))
    {
        calculateControl = $(calculateControl);
        if (calculateControl.length === 0 && typeof controlArg === "string" && idPrefix && idPrefix.length > 0)
        {
            calculateControl = $(controlArg.replace(/#/g, `#${idPrefix}`)) as JQuery<HTMLElement>;
        }
    }

    const control = $(calculateControl);

    let elements = $();
    let container;

    if (control.is(".codeEELValue,.tileViewEEL_value,.selectorEELInput,.codeEEL,.tileViewEEL,.selectorEEL"))
    {
        elements = elements.add(control.closest(".codeEEL,.tileViewEEL,.selectorEEL").find(".eELInputContainer__textBox,.ui-control-container,.eELInputContainer__button"));
    }
    else if (control.is(".htmlComboBox_value,.htmlComboBox"))
    {
        container = control.closest(".htmlComboBox");
        elements = elements.add(container);
        elements = elements.add(container.find(".htmlComboBox_input,.htmlComboBox_button"));
    }
    else if (control.is(".dmsBackingData,.distinctMultiSelectEEL"))
    {
        container = control.closest(".distinctMultiSelectEEL");
        elements = elements.add(container);
        elements = elements.add(container.find(".ui-control-container,.distinctMultiSelectUserInput"));
    }
    else if (control.is(".datepicker,.datepicker-wrapper"))
    {
        let datePicker = control;
        if (control.hasClass("datepicker-wrapper"))
        {
            datePicker = control.find(".datepicker");
        }

        datePicker.siblings('.datepicker-trigger').toggle(isEnabled);

        if (datePicker.hasClass("hasRelativeDatePicker"))
        {
            datePicker.prop("readonly", !isEnabled);
            const display = datePicker.closest(".datepicker-wrapper").siblings('.relativeDateDisplay');
            display.toggleClass("readonly", !isEnabled);
            elements = elements.add(display);
            datePicker.closest(".datepicker-wrapper").siblings(".launchRelativeDatePicker").toggle(isEnabled);
        }
        else
        {
            datePicker.datepicker("option", "disabled", !isEnabled);
            elements = elements.add(datePicker);
        }
    }
    else if (control.is(".relativeDate__positioning"))
    {
        const datePicker = control.find(".datepicker");
        const launchButton = control.find(".launchRelativeDatePicker");
        const display = control.find(".relativeDateDisplay");
        
        datePicker.prop("readonly", !isEnabled);
        datePicker.siblings(".datepicker-trigger").toggle(isEnabled);

        elements = elements.add(display);

        if (isEnabled)
        {
            display.removeClass("readonly");
            launchButton.show();
        }
        else
        {
            display.addClass("readonly");
            launchButton.hide();
        }
    }
    else if (control.is("sky-datepicker,sky-timepicker,sky-datetimepicker"))
    {
        if (isEnabled)
        {
            control.removeClass("ui-disabled");
            control.removeAttr("aria-readonly");
        }
        else
        {
            control.addClass("ui-disabled");
            control.attr("aria-readonly", "true");
        }
    }
    else if (control.is(".radioButtonGroupValue,.radioButtonGroup"))
    {
        elements = elements.add(control.find(":input"));
        if (isEnabled)
        {
            skyward.radioButtonGroup.enable(control);
        }
        else
        {
            skyward.radioButtonGroup.disable(control);
        }
    }
    else if (control.is(".radioButtonContent"))
    {
        elements = elements.add(control);
        if (isEnabled)
        {
            $.each(control, function ()
            {
                $(this).parent().removeClass("disabled");
            });
        }
        else
        {
            $.each(control, function ()
            {
                $(this).parent().addClass("disabled");
            });
        }
    }
    else if (control.hasClass('colorPicker'))
    {
        elements = elements.add(control.filter(".colorPicker"));
        if (isEnabled)
        {
            colorPicker.toggle(control, false);
        }
        else
        {
            colorPicker.toggle(control, true);
        }
    }
    else if (control.hasClass("multipleSelectBox"))
    {
        const checkboxes = control.find("input[type='checkbox']");
        const backingData = control.find(".multiSelectBackingData");

        const customValueEntry = control.siblings(".customValueEntry");
        if (customValueEntry.length > 0)
        {
            elements = elements.add(customValueEntry.find("input, a"));
        }

        elements = elements.add(control);
        elements = elements.add(checkboxes);
        elements = elements.add(backingData);
    }
    else if (control.is('a'))
    {
        control.css({ "pointer-events": isEnabled ? "" : "none" });
        elements = elements.add(control);
    }
    else if (control.is(".codeEELSelectedValueDisplayField"))
    {
        // do nothing.  This control shouldn't be toggled.
        // This is to work around a bug with dependency.
    }
    else if (htmlEditor.isHtmlEditorElement(control))
    {
        htmlEditor.toggle(control, isEnabled);
    }
    else if (control.is(':input'))
    {
        elements = elements.add(control);
    }
    else if (control.hasClass('checkboxGroup'))
    {
        elements = elements.add(control);
        elements = elements.add(control.find("input[type='checkbox']"));
    }

    const ariaReadOnlyControls = "select,.htmlComboBox,.radioButtonGroup,.radioButtonGroup__button,input[type='radio'],.checkboxGroup,.multipleSelectBox,input[type='checkbox'],sky-datepicker,sky-timepicker,sky-datetimepicker";
    const nativeReadOnlyControls = ".colorPicker,input[type='text'],input[type='password'],input[type='number'],input[type='email'],input[type='time'],textarea,.durationpicker";
    const readOnlyControls = ariaReadOnlyControls + "," + nativeReadOnlyControls;

    if (elements.length)
    {
        elements.not(ariaReadOnlyControls).prop("readonly", !isEnabled);
        elements.not(readOnlyControls).prop("disabled", !isEnabled);
        elements.toggleClass("ui-disabled", !isEnabled);
        elements.not(readOnlyControls).attr("aria-disabled", (!isEnabled).toString());
        elements.filter(ariaReadOnlyControls).attr("aria-readonly", (!isEnabled).toString());
        const defaultTabIndex = "0";

        if (!isEnabled)
        {
            elements.filter(ariaReadOnlyControls).each(function (this: HTMLElement)
            {
                const element = $(this);

                const describedBy = element.attr("aria-describedby");

                if (describedBy)
                {
                    if (describedBy.includes(readOnlyAnnouncementID))
                    {
                        return;
                    }

                    element.attr("aria-describedby", `${describedBy} ${readOnlyAnnouncementID}`);
                }
                else
                {
                    element.attr("aria-describedby", readOnlyAnnouncementID);
                }
            });
        }
        else
        {
            elements.filter(ariaReadOnlyControls).each(function (this: HTMLElement)
            {
                const element = $(this);

                const describedBy = element.attr("aria-describedby");
                if (!describedBy)
                {
                    return;
                }

                const filteredDescribedBy = describedBy.split(" ")
                    .filter(id => id !== readOnlyAnnouncementID)
                    .join(" ");

                element.attr("aria-describedby", filteredDescribedBy);
            });
        }

        //prevent focusing on disabled elements
        elements.not(readOnlyControls).filter(":input,a").each(function ()
        {
            const element = $(this);
            let origTabIndex = element.data("origTabIndex") as string | undefined;
            if (typeof origTabIndex === "undefined")
            {
                if (element.is(":input"))
                {
                    origTabIndex = defaultTabIndex;
                }
                else
                {
                    origTabIndex = element.attr("tabindex") || defaultTabIndex;
                }
                element.data("origTabIndex", origTabIndex);
            }
            element.attr("tabindex", isEnabled ? origTabIndex : "-1");
        });

        elements.trigger("readonly", isEnabled);
    }

    //return true if the passed in control matched a specific element type
    return elements.length > 0;
}

export function hide(identifier: JQueryInput, animate?: boolean, idPrefix?: string)
{
    const control = getControlToShowOrHide(identifier, idPrefix);
    if (!control || !control.get(0))
    {
        return;
    }

    if (control.is(".radioButtonGroupValue,.radioButtonGroup"))
    {
        skyward.radioButtonGroup.hide(control);
    }
    else if (control.hasClass("datepicker"))
    {
        skyward.datepicker.hide(control, animate);
    }
    else if (control.is(".datePicker,.timePicker,.dateTimePicker"))
    {
        const container = control.closest(".dateTimePickerWrapper");
        hideElement(container);
    }
    else if (control.is("option,optgroup"))
    {
        toggleOption(control, false);
    }
    else if (control.is(".codeEELValue,.tileViewEEL_value,.selectorEELInput"))
    {
        const container = control.closest(".codeEEL,.tileViewEEL,.selectorEEL");
        hideElement(container);
    }
    else if (control.hasClass("contentMessageArea"))
    {
        control.addClass("cmaHidden");
    }
    else if (htmlEditor.isHtmlEditorElement(control))
    {
        htmlEditor.toggleVisible(control, false, animate);
    }
    else
    {
        hideElement(control);
        control.trigger("visible", false);
    }

    function hideElement(element: JQuery<HTMLElement>)
    {
        if (animate !== false)
        {
            element.animateClose();
        }
        else
        {
            element.hide();
        }
    }
}

export function show(identifier: JQueryInput, animate?: boolean, idPrefix?: string)
{
    const control = getControlToShowOrHide(identifier, idPrefix);
    if (!control || !control.get(0))
    {
        return;
    }
    let animationControl;

    if (control.is(".radioButtonGroupValue,.radioButtonGroup"))
    {
        animationControl = skyward.radioButtonGroup.show(control);
    }
    else if (control.hasClass("datepicker"))
    {
        animationControl = skyward.datepicker.show(control, animate);
    }
    else if (control.is(".datePicker,.timePicker,.dateTimePicker"))
    {
        const container = control.closest(".dateTimePickerWrapper");
        showElement(container);
        animationControl = container;
    }
    else if (control.is("option,optgroup"))
    {
        toggleOption(control, true);
        animationControl = control;
    }
    else if (control.is(".codeEELValue,.tileViewEEL_value,.selectorEELInput"))
    {
        const container = control.closest(".codeEEL,.tileViewEEL,.selectorEEL");
        showElement(container);
        animationControl = container;
    }
    else if (control.hasClass("contentMessageArea"))
    {
        control.removeClass("cmaHidden");
        animationControl = control
    }
    else if (htmlEditor.isHtmlEditorElement(control))
    {
        animationControl = htmlEditor.toggleVisible(control, true, animate) ?? control;
    }
    else
    {
        showElement(control);
        animationControl = control;
    }
    animationControl.trigger("visible", true);
    animationControl.animationPromise().done(function ()
    {
        skyward.events.processVisibleActions(control);
    });

    function showElement(element: JQuery<HTMLElement>)
    {
        if (animate !== false)
        {
            element.animateOpen();
        }
        else
        {
            element.show();
        }
    }
}

export function toggleVisible(identifier: JQueryInput, visible: boolean, animate: boolean = true, idPrefix?: string)
{
    if (visible)
    {
        show(identifier, animate, idPrefix);
    }
    else
    {
        hide(identifier, animate, idPrefix);
    }
}

export function setComboBoxOptions(comboboxIdentifier: string | JQuery<HTMLElement>, options: any[], idPrefix?: string)
{
    let comboBox = typeof comboboxIdentifier == "string" ? $(`[name='${comboboxIdentifier}']`) : comboboxIdentifier;

    if (comboBox.length === 0)
    {
        comboBox = $(`[id="${comboboxIdentifier}"]`);
    }
    if (comboBox.length === 0 && idPrefix && idPrefix.length > 0)
    {
        comboBox = $(`[id="${idPrefix}${comboboxIdentifier}"]`);
    }
    comboBox.empty();

    //options must be an Array of objects to maintain sort order
    $.each(options, function (_index, value)
    {
        if (typeof value.Value == "string")
        {
            comboBox.append($("<option></option>").attr("value", value.Key).text(value.Value));
        }
        else
        {
            // If the value is not a string it will be an object that holds the value, selected, and dependents
            const option = $("<option></option>").attr("value", value.Key).text(value.Value.Value);

            if (value.Value.Dependents)
            {
                option.attr("data-dependents", JSON.stringify(value.Value.Dependents));
            }

            if (value.Value.Selected)
            {
                option.attr("Selected", "Selected");
            }

            comboBox.append(option);
        }
    });
}

export function getOriginalValuesData(selector: string)
{
    const controls = getCurrentValuesData(selector);
    return { controls: JSON.stringify(controls) };
}

export function getEditableFields(selector: JQuery<HTMLElement>)
{
    const hashList: { [key: string]: Record<string, string | number | string[] | undefined> } = {};
    selector.find(".editableFields").each(function ()
    {
        const hashContainer = $(this);
        const hash = hashContainer.data("hash");
        const value = hashContainer.val() as string;
        if (value)
        {
            const fields = JSON.parse(value);
            hashList[hash] = fields;
        }
    });
    return JSON.stringify(hashList);
}

export function valueHasChanged(elementArg: JQueryInput)
{
    const element = $(elementArg);

    const value = getValue(element);
    const data = element.data();

    if ($.isEmptyObject(data))
    {
        return false;
    }

    return data.originalValue != value;
}

export function getCurrentValuesData(selector: string)
{
    type ValueData = {
        key: string,
        value: any,
        skip: boolean
    }

    type ValueDataItem = {
        id: string,
        items: Array<ValueData>
    }

    const list: Array<ValueDataItem> = [];

    $(selector + " [data-original-value]").each(function ()
    {
        const item = $(this as HTMLElement);
        const name = item.attr("name");

        if (name != null)
        {
            let value = null;

            // Workflow refresh is the only time we save off the control state
            // On AJAX we remove all workflow control state information from pagestate
            if (selector == ".workflowPopup")
            {
                const data = item.data();

                if (item.is(":radio"))
                {
                    value = $(selector + " input[name='" + name + "']:checked").val();
                }
                else if (item.is(":checkbox"))
                {
                    value = item.is(":checked");
                }
                else if (item.hasClass("masked"))
                {
                    value = skyward.datamask.getUnmaskedValue(item);
                }
                else
                {
                    value = item.encodedVal();
                }

                if (value != null && value == data.originalValue)
                {
                    value = null;
                }
            }
            list.push({
                id: name,
                items: [{
                    key: "value",
                    value: value,
                    skip: false
                }]
            });
        }
    });

    return list;
}

export function clearValue(elementArg: JQueryInput, suppressChangeEvent = false)
{
    const element = $(elementArg);

    if (!suppressChangeEvent && element.hasClass("suppressChangeEvent"))
    {
        suppressChangeEvent = true;
    }

    if (element.is(":checkbox"))
    {
        element.prop("checked", false);
        if (!suppressChangeEvent)
        {
            element.trigger("change");
        }
    }
    else if (element.hasClass("datepicker"))
    {
        element.val('');
        if (!suppressChangeEvent)
        {
            element.trigger("change");
        }
    }
    else if (element.is(".datePicker,.dateTimePicker,sky-datepicker,sky-dateTimePicker"))
    {
        const container = element.is("sky-datepicker,sky-dateTimePicker") ? element : element.parent();
        const input = element.is("sky-datepicker,sky-dateTimePicker") ? element.find("input") : element;
        container.prop("valueAsDate", null);

        if (!suppressChangeEvent)
        {
            input.trigger("change");
        }
    }
    else if (element.is("label") || element.hasClass("visualLabel"))
    {
        element.text('');
        if (!suppressChangeEvent)
        {
            element.trigger("change");
        }
    }
    else if (element.hasClass("codeEEL"))
    {
        codeEel.setCodeEELValue(element, "", suppressChangeEvent);
    }
    else if (element.hasClass('distinctMultiSelectEEL') || element.hasClass("dmsBackingData"))
    {
        dmse.clear(element, suppressChangeEvent);
    }
    else if (element.hasClass('multiSelectBackingData'))
    {
        multipleSelectBox.clearValue(element, suppressChangeEvent);
    }
    else if (element.hasClass("tileViewEEL"))
    {
        const searchBox = element.find(".tileViewEEL_search");
        const searchData = searchBox.data();
        tileViewEEL.populateBoxes(element, searchData, "");
    }
    else if (element.hasClass("htmlComboBox"))
    {
        htmlComboBox.populateBoxes(element, null);
    }
    else if (element.hasClass('raceEthnicityVerification'))
    {
        element.find(':checkbox').prop('checked', false);
        element.trigger("change");
    }
    else if (element.hasClass('listSortOrder'))
    {
        element.val('[]');
        if (!suppressChangeEvent)
        {
            element.trigger("change");
        }
    }
    else if (htmlEditor.isHtmlEditorElement(element))
    {
        htmlEditor.clearValue(element, suppressChangeEvent);
    }
    else
    {
        remask(element, '');
        if (!suppressChangeEvent)
        {
            element.trigger("change");
        }
    }
}

export function changeId(elementArg: JQueryInput, idValue: string, nameValue: string)
{
    const element = $(elementArg);

    if (!element.data("original-id") && element.attr("id"))
    {
        element.data("original-id", element.attr("id")!);
    }
    element.attr('id', idValue);

    if (!nameValue)
    {
        nameValue = idValue;
    }

    if (element.attr('name'))
    {
        if (!element.data("original-name"))
        {
            element.data("original-name", element.attr("name")!);
        }
        element.attr('name', nameValue);
    }

    if (element.hasClass("radioButtonGroup"))
    {
        element.attr("data-name", nameValue);
        element.find("input[type='radio']").attr("name", nameValue + "child");
    }
}

export function changeCheckBoxWithSelectAll(currentCheckBox: JQuery<HTMLElement>, isSelectAllChecked: boolean)
{
    const currentState = currentCheckBox.is(':checked');

    if (currentCheckBox.hasClass("allowCodeChangesWhenReadonly") || !currentCheckBox.hasClass("ui-disabled"))
    {
        currentCheckBox.prop('checked', isSelectAllChecked);

        // Don't fire the onchange event if the checkbox was already checked
        // or if the checkbox was unchecked already
        if ((!currentState && isSelectAllChecked) || (currentState && !isSelectAllChecked))
        {
            currentCheckBox.trigger("change");
        }
    }
}

export function getScopeContainer(control: JQueryInput)
{
    const jqControl = $(control);
    const closest = jqControl.closest("#moduleContent, #detailsPanel, .workflowContent, body");
    if (closest.length)
    {
        return closest;
    }
    // Fallback for controls that aren't yet attached to the document
    return jqControl.parents().last();
}

registerEvents("controls",
    {
        hideoption: function (this: JQueryInput) { toggleOption($(this), false); }
    });

function remask(element: JQuery<HTMLElement>, value: string | number | string[] | undefined)
{
    if ((element.data("mask") || element.data("decimalMask")) && element.hasClass("masked"))
    {
        dataMaskSetValue(element, value);
    }
    else
    {
        element.val(value ?? "");
    }

    if (element.is("[data-year-mask]"))
    {
        applyYearMask(element);
    }
}

/**
 * Toggles the visible state of an <option> element.
 *
 * @param option - The option element to show or hide.
 * @param visible - True to show, false to hide.
 */
export function toggleOption(option: JQuery<HTMLElement>, visible: boolean)
{
    if (visible)
    {
        option.prop("disabled", false).css("display", "");
        const select = option.closest("select");
        if (select.children("option:not(:disabled)").length == 1)
        {
            setValue(select, option.val(), true);
        }
    }
    else
    {
        option.prop("disabled", true).css("display", "none");

        // Ensure the hidden option is not selected
        if (option.is(":selected"))
        {
            option.removeAttr("selected");
            const select = option.closest("select");
            const firstNonDisabledOption = select.find("option:not(:disabled)").first();
            setValue(select, firstNonDisabledOption.val(), true);
        }
    }
}

function getControlToShowOrHide(identifier: JQueryInput, idPrefix?: string)
{
    let control: JQuery<Element> | null | undefined;

    if (typeof identifier === "string")
    {
        try
        {
            control = $('[name="' + identifier + '"]');
        }
        catch (ex)
        {
            control = null;
        }

        if (!control || control.length === 0)
        {
            try
            {
                control = $("#" + identifier);
                if (control.length === 0 && idPrefix && idPrefix?.length > 0)
                {
                    control = $("#" + idPrefix + identifier);
                }
            }
            catch (ex)
            {
                control = null;
            }
        }
    }

    if (!control || control.length === 0)
    {
        try
        {
            control = $(identifier);
            if (control.length === 0 && typeof identifier === "string" && idPrefix && idPrefix?.length > 0)
            {
                control = $(identifier.replace("#", `#${idPrefix}`));
            }
        }
        catch (ex)
        {
            control = null;
        }
    }
    return control as JQuery<HTMLElement>;
}
